import React from "react";
import { useIntl } from "react-intl";

import { OutAdminProductExtendedViewDTO, OutAdminProductExtendedViewDTOCreatedByEnum } from "@app/api/generated";
import { useDateFnsFormat } from "@app/util/use-date-fns-format";
import { daysUntil } from "@app/util/product-lifecycle";

import { DescriptionPanel } from "./description-panel";
import styles from "./product-detail-sidebar.module.scss";

interface IProductDetailSidebarProps {
  product: OutAdminProductExtendedViewDTO;
  hasUnsavedChanges: boolean;
  onDescriptionGenerated: () => Promise<void>;
}

/** Right-hand sidebar on the product detail page: sync lifecycle details for this page. */
const ProductDetailSidebar = ({
  product,
  hasUnsavedChanges,
  onDescriptionGenerated
}: IProductDetailSidebarProps): JSX.Element => {
  const intl = useIntl();
  const { formatDate } = useDateFnsFormat();

  const isManual = product.createdBy !== OutAdminProductExtendedViewDTOCreatedByEnum.API;

  const renderCountdown = (date?: Date | null): JSX.Element => {
    if (!date) {
      return <>—</>;
    }
    const days = daysUntil(new Date(date));
    const exact = formatDate(new Date(date), "dd-MM-yyyy HH:mm");
    const text =
      days <= 0
        ? intl.formatMessage({ id: "products.detail.sidebar.today" })
        : intl.formatMessage({ id: "products.detail.sidebar.inDays" }, { days });
    return <span title={exact}>{text}</span>;
  };

  return (
    <aside className={styles.sidebar}>
      <h3 className={styles.heading}>{intl.formatMessage({ id: "products.detail.sidebar.heading" })}</h3>
      <dl className={styles.meta}>
        <div>
          <dt>{intl.formatMessage({ id: "products.detail.sidebar.pageType" })}</dt>
          <dd>
            {intl.formatMessage({
              id: isManual ? "products.createdBy.manual" : "products.createdBy.automatic"
            })}
          </dd>
        </div>
        <div>
          <dt>{intl.formatMessage({ id: "products.detail.sidebar.noDealsSince" })}</dt>
          <dd>{product.noDealsSince ? formatDate(new Date(product.noDealsSince), "dd-MM-yyyy") : "—"}</dd>
        </div>
        <div>
          <dt>{intl.formatMessage({ id: "products.detail.sidebar.goesOffline" })}</dt>
          <dd>{renderCountdown(product.offlineAt)}</dd>
        </div>
        <div>
          <dt>{intl.formatMessage({ id: "products.detail.sidebar.getsDeleted" })}</dt>
          <dd>{renderCountdown(product.deleteAt)}</dd>
        </div>
        <div>
          <dt>{intl.formatMessage({ id: "products.detail.sidebar.lastEdited" })}</dt>
          <dd>{product.updatedAt ? formatDate(new Date(product.updatedAt), "dd-MM-yyyy HH:mm") : "—"}</dd>
        </div>
      </dl>

      <hr className={styles.divider} />

      <DescriptionPanel product={product} hasUnsavedChanges={hasUnsavedChanges} onGenerated={onDescriptionGenerated} />
    </aside>
  );
};

export { ProductDetailSidebar };
